import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { BASE_PATH, STATIC_CATALOG } from "./constants";

import { hasValue } from "./util/Functions";
import { DB } from "./util/Cache.util";
import CatalogContext from "./contexts/CatalogContext";
import LoadMask from "./components/LoadMask";

import { useAuth } from "./auth/useAuth";
import ApiKeyContext from "./contexts/ApiKeyContext";
import ApiDrivenModel from "./models/ApiDrivenModel";
import Organization from "./models/Organization";
import OrganizationMember from "./models/OrganizationMember";
import IdentityClient from "./api/IdentityClient";
import { DialogContextProvider } from "./hooks/useDialogContext";
import { SidebarContextProvider } from "./hooks/useSidebarContext";
import Associate from "./models/Associate";
import useGlobalStyles from "./hooks/GlobalStyles";
import { Global } from "@emotion/react";
import Catalog from "./models/Catalog";
import { ColorUtil } from "./util/Color.util";

const PublicLayout = lazy(() => import("./layouts/public/PublicLayout"));
const ProtectedLayout = lazy(() => import("./layouts/protected/ProtectedLayout"));
const PrivateLayout = lazy(() => import("./layouts/private/PrivateLayout"));

const PWA = lazy(() => import("./components/PWA"));


function Appin() {
	const [catalog] = React.useContext(CatalogContext);
	const [template, setTemplate] = React.useState(STATIC_CATALOG ? 'private' : false);
	const [emailIsVerified, setEmailIsVerified] = React.useState(false);

	const urlSearchParams = new URLSearchParams(window.location.search);
	const redirect = urlSearchParams.get("redirect")
		&& decodeURIComponent(urlSearchParams.get("redirect"));

	const pathParts = window.location.pathname.trim('/').split('/').filter(hasValue);
	const { currentUser, setCurrentUser, checkAuth, organizationMember, setOrganizationMember } = useAuth();
	const protectedPageRoots = ['user', 'catalog', 'email-verification'];
	const publicPageRoots = ['login', 'register', 'reset-password', 'pin-login', 'public'];

	React.useEffect(() => {
		const currentUserInfo = JSON.parse(localStorage.getItem('organization_member:data'));
		const branding = currentUserInfo?.organization?.branding;
		let orgForegroundColor = branding?.themes?.header?.foreground?.color || undefined;
		let orgBackgroundColor = branding?.themes?.header?.background?.color || undefined;
		const catalogForegroundColor = catalog?.options?.appearance?.header?.color || catalog?.options?.appearance?.header?.font_color || "#000000";
		const catalogBackgroundColor = catalog?.options?.appearance?.header?.background_color || "#FFFFFF";

		// need these in body because modals and sidebars are created outside #boss container
		const body = document.body;
		body.classList.toggle("retailer-only-branded", Catalog.Is_Retailer_Only_Branded(catalog));
		// assign init styles to :root/html so they can be overridden later in body for retailer only branding
		const root = document.querySelector("html");

		root.style.setProperty("--catalog-foreground-color", catalogForegroundColor);
		root.style.setProperty("--catalog-background-color", catalogBackgroundColor);

		const [darkerCatalogColor, lighterCatalogColor] = ColorUtil.SortColors(catalogForegroundColor, catalogBackgroundColor);
		root.style.setProperty("--catalog-darker-color", darkerCatalogColor);
		root.style.setProperty("--catalog-lighter-color", lighterCatalogColor);

		orgForegroundColor ||= "#000000";
		orgBackgroundColor ||= "#ffffff";
		root.style.setProperty("--org-foreground-color", orgForegroundColor);
		root.style.setProperty("--org-background-color", orgBackgroundColor);

		const [darkerOrgColor, lighterOrgColor] = ColorUtil.SortColors(orgForegroundColor, orgBackgroundColor);

		root.style.setProperty("--org-darker-color", darkerOrgColor);
		root.style.setProperty("--org-lighter-color", lighterOrgColor);
	}, [catalog, organizationMember]);


	const API_KEY_CONTEXT = React.useContext(ApiKeyContext);
	ApiDrivenModel.CURRENT_CONTEXT = API_KEY_CONTEXT;

	const fetch_organization_member = async () => {
		const makeOrgDbName = id => id != null ? DB.Make_Name(id, "org") : undefined;
		const makeMemberKey = id => id != null ? ['organizationMember', id] : undefined;

		let localOrgMember = JSON.parse(localStorage.getItem('organization_member:data') || false);
		const orgDbName = makeOrgDbName(localOrgMember?.organization?.id);
		const memberKey = makeMemberKey(localOrgMember?.id);

		let member;
		if (orgDbName && memberKey) {
			const memberData = await DB.Get(orgDbName, memberKey);
			// TODO ... if data should be instantiated, store with class name ...  but then figure out how to do that
			return new OrganizationMember(memberData);
		}

		member = await OrganizationMember.FetchBy({
			filter: [{
				property: '{profile}',
				value: "@profile_id"
			}],
			paths: {
				id: true,
				profile: {
					id: true,
					username: true,
					email: true,
					given_name: true,
					family_name: true,
					pronouns: true,
					picture: true,
					mobile_phone: true,
					last_activity: true,
					public_token: true,
				},
				organization: {
					id: true,
					active: true,
					address: true,
					administrative_area: true,
					country: true,
					formatted_address: true,
					locality: true,
					name: true,
					postal_code: true,
					sub_administrative_area: true,
					logo: true,
					icon: true,
					branding: true,
					features: true,
					handle: true,
					parent: {
						id: true,
						logo: true,
						icon: true,
						branding: true,
						features: true,
						handle: true,
					}
				},
				role: {
					id: true,
					is_administrator: true,
					label: true,
					access: true
				},
				verified: true
			},
		}, null, true)

		if (member) {
			// Set for 15 minutes
			DB.Set(makeOrgDbName(member.organization.id), makeMemberKey(member.id), member, 60 * 15);
			return member;
		}

		/*
				TODO !!!!!!!!!
				the Identity authorize call always brings back "organization" as 0
				so we aren't getting an accurate org id to store Associate info
				fix, and put cache back
		 */

		let associate;
		// const associateKey = ['associate', currentUser.email];
		// let associate = await DB.Get(makeOrgDbName(currentUser.organization), associateKey);
		// if (associate) {
		// 	associate = new Associate(associate);
		// }

		if (!associate) {
			associate = await Associate.FetchBy({ filter: [{ property: "email", value: currentUser.email }] });
			// DB.Set(makeOrgDbName(currentUser.organization), associateKey, associate);
		}

		if (!associate?.account?.organization) {
			return;
		}
		const organization = new Organization();
		organization.setProperties({ id: associate.account.organization });
		const { success } = await organization.linkAssociate(associate.id);
		success && window.location.reload();
	}
	
	if (currentUser?.email_verified && window.location.pathname === "/email-verification") {
		window.location = redirect || '/catalog/select';
	}

	React.useEffect(() => {
		//if localStorage isn't a proper object, then delete it.
		try {
			let localOrgMember = localStorage.getItem('organization_member:data');
			if (!JSON.parse(localOrgMember) || typeof JSON.parse(localOrgMember) !== 'object') {
				throw new TypeError('error with localStorage')
			}
		} catch (e) {
			localStorage.removeItem('organization_member:data');
		}

		//this usually won't run because localStorage is set upon logging in. 
		//this is just in case localStorage is edited by user, or localStorage is holding invalid JSON object
		if (currentUser) {
			const stored_member = localStorage.getItem("organization_member:data");
			if (stored_member) {
				const parsedMember = JSON.parse(stored_member);
				setOrganizationMember(parsedMember?.id ? parsedMember : undefined);
			}

			fetch_organization_member().then(response => {
				if (response) {
					response.cleanForSave();
					delete response._system;
					if (response?.id) {
						localStorage.setItem('organization_member:data', JSON.stringify(response));
						setOrganizationMember(response);
					}
				}
			}).catch(e => {
				// Ignore duplicate member errors
			});
		}

		const getTemplate = () => {
			if (STATIC_CATALOG) {
				return setTemplate("private");
			}
			if (!pathParts.length || publicPageRoots.includes(pathParts[0])) {
				// if (!currentUser || !pathParts.length || publicPageRoots.includes(pathParts[0])) {
				return setTemplate("public");
			}
			return setTemplate(
				!pathParts.length || protectedPageRoots.includes(pathParts[0])
					? "protected"
					: "private"
			);
		}
		getTemplate();
		
		if (currentUser && window.location.pathname === "/login") {
			window.location = redirect || '/catalog/select';
		}

	}, [currentUser]);
	
	React.useEffect(() => {
		if (currentUser?.email_verified && window.location.pathname === "/email-verification") {
			window.location = redirect || '/catalog/select';
		}
	}, [currentUser?.email_verified]);

	const getModule = template => {
		switch (template) {
			case 'public':
				return (<PublicLayout />);
			case 'protected':
				return (<ProtectedLayout />);
			case 'private':
				return (
					<SidebarContextProvider>
						<PrivateLayout />
					</SidebarContextProvider>
				);
			default: return null;
		}
	};

	const wrapperClasses = [
		"bossLiveCatalogWrapper",
		catalog?.options?.type && "type-" + catalog.options.type,
		(((catalog !== null) || (template !== "private")) && template)
	];

	const GlobalStyles = useGlobalStyles(catalog?.options?.appearance);

	
	
	if (currentUser && urlSearchParams.get('email-verification') && !emailIsVerified) {
		const verificationCode = urlSearchParams.get('email-verification');
		IdentityClient.post(
			"email_verification_response",
			{
				response: {
					username: currentUser.username,
					verification_token: verificationCode
				}
			}
		).then(response => {
			if (!response.data.success) {
				return;
			}
			setEmailIsVerified(true)
			currentUser.email_verified = true;
			urlSearchParams.delete('email-verification');
		});

		return <LoadMask show={true} />;
	}

	const auth = urlSearchParams.get("auth");
	if (auth) {
		checkAuth(auth).then(() => {
			// Remove param and reload
			const url = new URL(window.location.href);
			urlSearchParams.delete("auth");
			url.search = urlSearchParams.toString();
			window.location.href = url.toString();
		});
		return (<></>);
	}
	return (
		<>
			<Global styles={GlobalStyles} />
			<div className="_boss" id="_boss">
				<DialogContextProvider>
					<div className={wrapperClasses.filter(a => a).join(" ")}>
						<Router basename={BASE_PATH}>
							<Suspense fallback={<LoadMask show={true} />}>
								{getModule(template)}
							</Suspense>
						</Router>
					</div>
				</DialogContextProvider>
			</div>

			{template && template !== "public" && (
				<React.Suspense fallback={<></>}>
					<PWA />
				</React.Suspense>
			)}
		</>
	);
}

export default Appin;
